import {
  Autocomplete,
  AutocompleteProps,
  AutocompleteValue,
  AvatarProps,
  BaseTextFieldProps,
  InputAdornment,
  InputBaseProps,
  TextField,
  useForkRef,
} from '@mui/material';
import { SyntheticEvent, useCallback, useEffect } from 'react';
import { Control, FieldPath, FieldValues, RegisterOptions, useController } from 'react-hook-form';
import { useFieldStateError } from './useFieldStateError';

export type FormAutocompleteFieldProps<
  Value = string,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<
  AutocompleteProps<Value, Multiple, DisableClearable, false>,
  'value' | 'defaultValue' | 'disabled' | 'onBlur' | 'onChange' | 'renderInput'
> &
  Pick<BaseTextFieldProps, 'id' | 'label' | 'inputRef' | 'helperText' | 'autoFocus'> & {
    name: TName;
    control?: Control<TFieldValues>;
    options: Value[];
    InputProps?: InputBaseProps;
    renderAvatar?: (value: Value, avatarProps: Partial<AvatarProps>) => React.ReactNode;
  } & Pick<RegisterOptions<TFieldValues, TName>, 'required' | 'disabled' | 'validate'>;

export function FormAutocompleteField<
  Value = string,
  Multiple extends boolean = false,
  DisableClearable extends boolean = false,
>({
  id,
  name,
  label,
  control,
  required,
  inputRef,
  helperText,
  autoFocus,
  renderAvatar: renderStartAdornment,
  ...props
}: FormAutocompleteFieldProps<Value, Multiple, DisableClearable>) {
  const { field, fieldState } = useController({
    name,
    control,
    disabled: props.disabled,
    rules: {
      required,
    },
  });

  useEffect(() => {
    if (
      !props.multiple &&
      !props.loading &&
      props.options.length &&
      field.value &&
      !props.options.includes(field.value)
    ) {
      field.onChange(null);
    }
  }, [field, props.loading, props.options, props.multiple]);

  const error = useFieldStateError(label, fieldState.error);
  const forkedInputRef = useForkRef(field.ref, inputRef);

  const handleChange = useCallback(
    (_: SyntheticEvent, value: AutocompleteValue<Value, Multiple, DisableClearable, false>) => {
      field.onChange(value);
    },
    [field],
  );

  const startAdornment =
    renderStartAdornment && field.value ? (
      <InputAdornment position="start">
        {renderStartAdornment(field.value, { size: 'small' })}
      </InputAdornment>
    ) : undefined;

  return (
    <Autocomplete<Value, Multiple, DisableClearable, false>
      autoHighlight
      {...props}
      // controlled component must have value
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      value={field.value ?? null}
      disabled={field.disabled}
      onBlur={field.onBlur}
      onChange={handleChange}
      renderInput={(params) => (
        <TextField
          {...params}
          id={id}
          // name={field.name}
          label={label}
          required={Boolean(required)}
          error={fieldState.invalid}
          helperText={error ?? helperText}
          inputRef={forkedInputRef}
          autoFocus={autoFocus}
          slotProps={{
            input: { ...params.InputProps, startAdornment },
            htmlInput: { ...params.inputProps, 'data-1p-ignore': true },
          }}
        />
      )}
    />
  );
}
